import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './App.css';

// Import pages
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import Users from './pages/Users';
import UserDetail from './pages/UserDetail';
import Devices from './pages/Devices';
import DeviceDetail from './pages/DeviceDetail';
import Notifications from './pages/Notifications';
import Banners from './pages/Banners';
import FAQs from './pages/FAQs';
import Policies from './pages/Policies';
import SerialKeys from './pages/SerialKeys';
import AdminUsers from './pages/AdminUsers';
import Permissions from './pages/Permissions';
import IPWhitelist from './pages/IPWhitelist';
import AccessLogs from './pages/AccessLogs';

// Navigation items with permissions
const NAV_ITEMS = [
  { path: '/dashboard', label: '📊 대시보드', permission: null },
  { path: '/analytics', label: '📈 신규 가입자 현황', permission: 'analytics.view' },
  { type: 'divider' },
  { path: '/users', label: '👥 사용자 관리', permission: 'users.view' },
  { path: '/devices', label: '📱 디바이스 관리', permission: 'devices.view' },
  { path: '/serial-keys', label: '🔑 시리얼 키 관리', permission: 'serial_keys.view' },
  { type: 'divider' },
  { path: '/notifications', label: '🔔 알림 관리', permission: 'notifications.view' },
  { path: '/banners', label: '🎯 배너 관리', permission: 'banners.view' },
  { path: '/faqs', label: '❓ FAQ 관리', permission: 'faqs.view' },
  { path: '/policies', label: '📋 정책 관리', permission: 'policies.view' },
  { type: 'divider' },
  { path: '/admin-users', label: '👤 관리자 계정', permission: 'admin_users.view' },
  { path: '/permissions', label: '🔒 권한 관리', permission: 'permissions.view' },
  { path: '/ip-whitelist', label: '🛡️ IP 접근 제어', permission: 'ip_whitelist.view' },
  { path: '/access-logs', label: '📝 접근 로그', permission: 'access_logs.view' },
];

// Sidebar Component
const Sidebar = () => {
  const { user, logout, hasPermission, authType } = useAuth();
  const location = useLocation();

  return (
    <nav className="sidebar">
      <div className="sidebar-header">
        <h2>Puraxel Backoffice</h2>
        {user && (
          <div className="user-info">
            <span className="user-name">{user.full_name || user.username}</span>
            <span className="user-role">
              {authType === 'apikey' ? '🔑 API Key' : `👤 ${user.role}`}
            </span>
          </div>
        )}
      </div>
      
      <ul className="nav-links">
        {NAV_ITEMS.map((item, index) => {
          if (item.type === 'divider') {
            return <li key={`divider-${index}`} className="nav-divider" />;
          }

          // Allow all users to see Dashboard and Analytics
          if (item.permission && item.permission !== 'analytics.view' && !hasPermission(item.permission)) {
            return null;
          }

          const isActive = location.pathname === item.path || 
                          (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

          return (
            <li key={item.path} className={isActive ? 'active' : ''}>
              <Link to={item.path}>{item.label}</Link>
            </li>
          );
        })}
      </ul>
      
      <div className="sidebar-footer">
        <button onClick={logout} className="logout-btn">🚪 로그아웃</button>
      </div>
    </nav>
  );
};

// Main Layout
const MainLayout = ({ children }) => (
  <div className="app">
    <Sidebar />
    <main className="main-content">{children}</main>
  </div>
);

// Protected Route
const ProtectedRoute = ({ children, permission }) => {
  const { isAuthenticated, loading, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="loading-screen"><div className="loading-spinner">Loading...</div></div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Dashboard and Analytics are accessible to all authenticated users
  if (permission && permission !== 'analytics.view' && !hasPermission(permission)) {
    return (
      <MainLayout>
        <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
          <h2 style={{ color: '#e74c3c', marginBottom: '15px' }}>🔒 접근 거부</h2>
          <p style={{ color: '#7f8c8d' }}>이 페이지에 접근할 권한이 없습니다.</p>
          <p style={{ color: '#95a5a6', fontSize: '14px', marginTop: '10px' }}>
            필요 권한: <code>{permission}</code>
          </p>
          <button className="btn btn-primary" style={{ marginTop: '20px' }} onClick={() => window.history.back()}>
            ← 뒤로 가기
          </button>
        </div>
      </MainLayout>
    );
  }

  return <MainLayout>{children}</MainLayout>;
};

// App Routes
const AppRoutes = () => {
  const { isAuthenticated, user, loading, login, logout } = useAuth();
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    if (user?.must_change_password) {
      setMustChangePassword(true);
    }
  }, [user]);

  if (loading) {
    return <div className="loading-screen"><div className="loading-spinner">Loading...</div></div>;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="*" element={
          <Login onLogin={(result) => {
            login(result);
            if (result.mustChangePassword) setMustChangePassword(true);
          }} />
        } />
      </Routes>
    );
  }

  if (mustChangePassword && user) {
    return (
      <ChangePassword 
        user={user}
        onPasswordChanged={() => setMustChangePassword(false)}
        onLogout={logout}
      />
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute permission="analytics.view"><Analytics /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute permission="users.view"><Users /></ProtectedRoute>} />
      <Route path="/users/:id" element={<ProtectedRoute permission="users.view"><UserDetail /></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute permission="devices.view"><Devices /></ProtectedRoute>} />
      <Route path="/devices/:id" element={<ProtectedRoute permission="devices.view"><DeviceDetail /></ProtectedRoute>} />
      <Route path="/serial-keys" element={<ProtectedRoute permission="serial_keys.view"><SerialKeys /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute permission="notifications.view"><Notifications /></ProtectedRoute>} />
      <Route path="/banners" element={<ProtectedRoute permission="banners.view"><Banners /></ProtectedRoute>} />
      <Route path="/faqs" element={<ProtectedRoute permission="faqs.view"><FAQs /></ProtectedRoute>} />
      <Route path="/policies" element={<ProtectedRoute permission="policies.view"><Policies /></ProtectedRoute>} />
      <Route path="/admin-users" element={<ProtectedRoute permission="admin_users.view"><AdminUsers /></ProtectedRoute>} />
      <Route path="/permissions" element={<ProtectedRoute permission="permissions.view"><Permissions /></ProtectedRoute>} />
      <Route path="/ip-whitelist" element={<ProtectedRoute permission="ip_whitelist.view"><IPWhitelist /></ProtectedRoute>} />
      <Route path="/access-logs" element={<ProtectedRoute permission="access_logs.view"><AccessLogs /></ProtectedRoute>} />
      
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;